/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { Text } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import type { SuggestionAgent } from '@atlassian/editor-plugin-ai-suggestions';

export type SuggestionAgentIdentityProps = {
	agent?: SuggestionAgent | null;
};

const styles = cssMap({
	row: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		height: token('space.250'),
		minWidth: 0,
	},
	agentName: {
		minWidth: 0,
	},
});

function RovoIdentity(): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.row}>
			<RovoHexIcon label="" size="xxsmall" />
			<Text size="small" weight="regular" color="color.text.subtle">
				{formatMessage(aiSuggestionsMessages.cardRovoTitle)}
			</Text>
		</div>
	);
}

export function SuggestionAgentIdentity({
	agent,
}: SuggestionAgentIdentityProps): React.JSX.Element {
	const name = agent?.name?.trim();

	if (!agent?.accountId || !name) {
		return <RovoIdentity />;
	}

	return (
		<div css={styles.row}>
			<AgentAvatar
				imageUrl={agent?.picture?.trim() || undefined}
				agentIdentityAccountId={agent.accountId}
				size="xsmall"
				label={name}
				showBorder={false}
			/>
			<div css={styles.agentName}>
				<Text size="small" weight="regular" color="color.text.subtle" maxLines={1}>
					{name}
				</Text>
			</div>
		</div>
	);
}
